/**
 * Homepage chat search bar — entry point on WorkflowTypeSelectorPage.
 *
 * Visual chrome reuses `MessageInputShell` from FarChat (paperclip on left,
 * autosizing textarea in the middle, Send icon button on the right) so this
 * surface reads as the same control the user already knows from the
 * workflow chatbot popup.
 *
 * Behaviorally, the bar owns:
 *   - text draft
 *   - barSessionId (lazily allocated via POST /sessions on first file pick)
 *   - chips (per-file: status uploading/ready/error, AbortController, etc.)
 *   - drag-over state via `useDragDropUpload`
 *
 * It does NOT open the chat popup itself — that's the launcher's job. On
 * Enter (or Send click) it calls `props.onSubmit(text, barSessionId)` and
 * resets local state. Validation/network/guard errors call `props.onError`.
 *
 * Spec: issues/20260430-homepage-chat-popup_spec.md §4.2 / §4.4 Path A.
 * Implplan: issues/20260430-homepage-chat-popup_implplan.md §3 step 3.
 */
import React, { useCallback, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import MessageInputShell from '../FarChat/MessageInputShell';
import AttachmentChip from '../FarChat/AttachmentChip';
import DropOverlay from '../FarChat/DropOverlay';
import useDragDropUpload from '../FarChat/useDragDropUpload';
import {
  createSession as apiCreateSession,
  uploadAttachment as apiUploadAttachment,
  deleteAttachment as apiDeleteAttachment,
} from './api';

const MAX_FILES = 5;
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB
const ACCEPTED_MIMES = ['application/pdf', 'image/png', 'image/jpeg'];
const ACCEPT_ATTR = ACCEPTED_MIMES.join(',');

// jsdom doesn't ship `crypto.randomUUID` everywhere; fall back to a simple
// non-cryptographic id. The id is only used for React keys + chip identity.
// The monotonic counter guarantees uniqueness even when randomUUID is
// stubbed to a constant in tests.
let chipCounter = 0;
function makeLocalId(): string {
  chipCounter += 1;
  if (
    typeof crypto !== 'undefined' &&
    typeof (crypto as any).randomUUID === 'function'
  ) {
    return `${(crypto as any).randomUUID()}-${chipCounter}`;
  }
  return `chip-${Date.now()}-${chipCounter}`;
}

type ChipStatus = 'uploading' | 'ready' | 'error';

interface Chip {
  localId: string;
  /**
   * The filename the chip displays in the UI. Starts as the local File
   * name; on successful upload it's replaced by the server-returned
   * filename (so DELETE targets the right path).
   */
  filename: string;
  /** Cached MIME / size for chip display. */
  size: number;
  mime_type: string;
  status: ChipStatus;
  /** Captured at upload time so chip × can abort an in-flight POST. */
  abortController?: AbortController;
  /** Last-known upload error message (surfaced via the error icon tooltip). */
  errorMessage?: string;
  /** Original File handle, retained for retry. */
  file: File;
}

export interface HomeChatSearchBarProps {
  onSubmit: (message: string, sessionId: string | null) => void;
  onError?: (message: string) => void;
  /** Optional controlled draft used by the homepage starter actions. */
  draft?: string;
  onDraftChange?: (message: string) => void;
  inputRef?: React.Ref<HTMLTextAreaElement>;
  placeholder?: string;
  /**
   * Workflow-only callers may type an intake request, but must not stage
   * arbitrary Home Chat attachments.  Default keeps the normal CO/CS
   * composer byte-for-byte capable of uploads.
   */
  allowAttachments?: boolean;
}

const HomeChatSearchBar: React.FC<HomeChatSearchBarProps> = ({
  onSubmit,
  onError,
  draft,
  onDraftChange,
  inputRef,
  placeholder = "Ask a FAR question\u2026",
  allowAttachments = true,
}) => {
  const [internalText, setInternalText] = useState("");
  const [barSessionId, setBarSessionId] = useState<string | null>(null);
  const [chips, setChips] = useState<Chip[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Mirror chips/barSessionId in refs so async handlers see the freshest
  // values without re-binding the validate/upload pipeline on every render.
  const chipsRef = useRef<Chip[]>(chips);
  chipsRef.current = chips;
  const barSessionIdRef = useRef<string | null>(barSessionId);
  barSessionIdRef.current = barSessionId;
  const text = draft ?? internalText;
  const setText = onDraftChange ?? setInternalText;

  const reportError = useCallback(
    (message: string) => {
      if (onError) onError(message);
    },
    [onError]
  );

  // Validate a single file against the count/size/MIME limits. Returns null
  // if the file is acceptable; otherwise a human-readable rejection reason.
  const validateFile = useCallback(
    (file: File, currentChipCount: number): string | null => {
      if (currentChipCount >= MAX_FILES) {
        return `Up to ${MAX_FILES} files allowed.`;
      }
      if (file.size > MAX_FILE_SIZE) {
        return `${file.name} exceeds the 20 MB limit.`;
      }
      // If the browser left .type empty, fall back to extension sniffing —
      // matches `useDragDropUpload`'s behavior so drag-drop and paperclip
      // pick agree on what's accepted.
      if (file.type) {
        if (!ACCEPTED_MIMES.includes(file.type)) {
          return `${file.name}: only PDF, PNG, and JPEG files are supported.`;
        }
      } else {
        const lower = file.name.toLowerCase();
        const ok =
          lower.endsWith('.pdf') ||
          lower.endsWith('.png') ||
          lower.endsWith('.jpg') ||
          lower.endsWith('.jpeg');
        if (!ok) {
          return `${file.name}: only PDF, PNG, and JPEG files are supported.`;
        }
      }
      return null;
    },
    []
  );

  // Upload a single file against the (now-known) bar session. Mutates the
  // matching chip via setChips on success/failure. Used both for the
  // initial pick path and the retry path.
  const performUpload = useCallback(
    async (chip: Chip, sessionId: string): Promise<void> => {
      try {
        const result = await apiUploadAttachment(sessionId, chip.file, {
          signal: chip.abortController?.signal,
        });
        // Replace the chip's display filename with the server response so
        // DELETE later targets the right URL.
        setChips((prev) =>
          prev.map((c) =>
            c.localId === chip.localId
              ? {
                  ...c,
                  status: 'ready' as ChipStatus,
                  filename: result.filename,
                  size: result.size,
                  mime_type: result.mime_type,
                  abortController: undefined,
                  errorMessage: undefined,
                }
              : c
          )
        );
      } catch (err: any) {
        // AbortError happens when the user clicks × during upload; the chip
        // has already been removed in the click handler so we drop the
        // failure on the floor.
        const isAbort =
          (err && (err.name === 'AbortError' || err.name === 'Aborted')) ||
          chip.abortController?.signal.aborted;
        if (isAbort) return;
        const msg = err?.message || 'Upload failed';
        setChips((prev) =>
          prev.map((c) =>
            c.localId === chip.localId
              ? {
                  ...c,
                  status: 'error' as ChipStatus,
                  abortController: undefined,
                  errorMessage: msg,
                }
              : c
          )
        );
        reportError(`Upload failed: ${chip.file.name}`);
      }
    },
    [reportError]
  );

  // Accept a list of File objects (from paperclip pick or drag-drop) and
  // run the validate → ensure-session → upload pipeline sequentially.
  const handleFiles = useCallback(
    async (files: File[]) => {
      if (!files || files.length === 0) return;
      // Walk files sequentially. Each iteration sees the latest chip count
      // via chipsRef so the count guard rejects subsequent files once
      // we hit MAX_FILES.
      for (const file of files) {
        const currentCount = chipsRef.current.length;
        const reason = validateFile(file, currentCount);
        if (reason) {
          reportError(reason);
          continue;
        }

        // Ensure barSessionId exists. If we don't have one yet, allocate.
        let sid = barSessionIdRef.current;
        if (!sid) {
          try {
            const resp = await apiCreateSession();
            sid = resp.session_id;
            barSessionIdRef.current = sid;
            setBarSessionId(sid);
          } catch (err: any) {
            reportError(
              err?.message || "Couldn't create chat session. Try again."
            );
            // Abort the rest of this batch — without a session, nothing
            // else in this drop/pick can succeed either.
            return;
          }
        }

        const controller = new AbortController();
        const newChip: Chip = {
          localId: makeLocalId(),
          filename: file.name,
          size: file.size,
          mime_type: file.type || 'application/octet-stream',
          status: 'uploading',
          abortController: controller,
          file,
        };
        // Push the chip first so the spinner shows immediately.
        chipsRef.current = [...chipsRef.current, newChip];
        setChips((prev) => [...prev, newChip]);
        // eslint-disable-next-line no-await-in-loop
        await performUpload(newChip, sid);
      }
    },
    [validateFile, reportError, performUpload]
  );

  // Drag-and-drop wiring. The hook is shared with FarChat — we route
  // dropped files through the same validation pipeline as paperclip pick.
  const { dragHandlers, isDragging } = useDragDropUpload({
    disabled: !allowAttachments,
    onFiles: (files) => {
      void handleFiles(files);
    },
    accept: ACCEPTED_MIMES,
  });

  // ---- Paperclip flow ------------------------------------------------------

  const onAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const onPickFile = useCallback(
    async (ev: React.ChangeEvent<HTMLInputElement>) => {
      const list = ev.target.files;
      const files = list ? Array.from(list) : [];
      // Always clear so picking the same file twice still triggers change.
      if (fileInputRef.current) fileInputRef.current.value = '';
      await handleFiles(files);
    },
    [handleFiles]
  );

  // ---- Chip × ---------------------------------------------------------------

  const handleRemoveChip = useCallback(
    async (chip: Chip) => {
      if (chip.status === 'uploading') {
        // Cancel the in-flight upload. The performUpload catch swallows
        // AbortError so no error state leaks.
        chip.abortController?.abort();
        setChips((prev) => prev.filter((c) => c.localId !== chip.localId));
        return;
      }
      if (chip.status === 'error') {
        // Nothing on the server yet — just drop it locally.
        setChips((prev) => prev.filter((c) => c.localId !== chip.localId));
        return;
      }
      // ready: the file is on the server. Pessimistic delete: keep the chip
      // visible during the in-flight DELETE, then remove locally on success.
      const sid = barSessionIdRef.current;
      if (!sid) {
        // Defensive: if we have a ready chip we must have a session, but
        // never crash.
        setChips((prev) => prev.filter((c) => c.localId !== chip.localId));
        return;
      }
      try {
        await apiDeleteAttachment(sid, chip.filename);
        setChips((prev) => prev.filter((c) => c.localId !== chip.localId));
      } catch (err: any) {
        reportError(err?.message || 'Failed to remove file');
      }
    },
    [reportError]
  );

  // ---- Chip retry ----------------------------------------------------------

  const handleRetryChip = useCallback(
    async (chip: Chip) => {
      const sid = barSessionIdRef.current;
      if (!sid) {
        // Shouldn't happen — error chips imply a successful session create.
        return;
      }
      const controller = new AbortController();
      // Flip back to uploading and swap in the fresh AbortController.
      const refreshed: Chip = {
        ...chip,
        status: 'uploading',
        abortController: controller,
        errorMessage: undefined,
      };
      setChips((prev) =>
        prev.map((c) => (c.localId === chip.localId ? refreshed : c))
      );
      await performUpload(refreshed, sid);
    },
    [performUpload]
  );

  // ---- Submit (Enter or Send click) ----------------------------------------

  const handleSubmit = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed) return; // empty / whitespace → no-op
    // Guard: any non-ready chip blocks submit.
    const hasInFlight = chipsRef.current.some(
      (c) => c.status === 'uploading' || c.status === 'error'
    );
    if (hasInFlight) {
      reportError('Wait for uploads to finish, or remove the failed file.');
      return;
    }
    onSubmit(trimmed, barSessionIdRef.current);
    // Reset bar state for the next submission.
    setText('');
    setChips([]);
    chipsRef.current = [];
    setBarSessionId(null);
    barSessionIdRef.current = null;
  }, [text, onSubmit, reportError, setText]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      } else if (e.key === 'Escape' && text.length > 0) {
        e.preventDefault();
        setText('');
      }
    },
    [handleSubmit, setText, text],
  );

  // The shell disables Send when text is empty. Chip-state guards run inside
  // handleSubmit (Enter or Send click), so we don't need to disable the
  // textarea while chips are pending — the user might still want to type
  // their question while uploads are in flight.

  return (
    <Box
      data-testid="home-chat-search-bar"
      sx={{
        position: 'relative',
        // The parent launcher owns the responsive width; the bar fills its
        // full usable width.
        width: '100%',
      }}
      {...(allowAttachments ? dragHandlers : {})}
    >
      {/* Hidden file input wired to the shell's paperclip click. */}
      {allowAttachments && <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPT_ATTR}
        data-testid="home-chat-file-input"
        style={{ display: 'none' }}
        onChange={onPickFile}
      />}

      {/* Chip row — only rendered when there's at least one chip. */}
      {allowAttachments && chips.length > 0 && (
        <Box
          data-testid="home-chat-chip-row"
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            px: 1.5,
            py: 0.75,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          {chips.map((c) => (
            <AttachmentChip
              key={c.localId}
              attachment={{
                filename: c.filename,
                size: c.size,
                mime_type: c.mime_type,
              }}
              status={c.status}
              errorMessage={c.errorMessage}
              onRemove={() => {
                void handleRemoveChip(c);
              }}
              onRetry={
                c.status === 'error'
                  ? () => {
                      void handleRetryChip(c);
                    }
                  : undefined
              }
            />
          ))}
        </Box>
      )}

      {/* Composer chrome (paperclip + textarea + Send). The shell disables
          Send when text is empty; we also pass `disabled` so chip-state
          guards win without the shell needing to know about chips. */}
      <MessageInputShell
        value={text}
        onChange={setText}
        onSend={handleSubmit}
        onAttachClick={onAttachClick}
        showAttachments={allowAttachments}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        inputRef={inputRef}
      />

      {/* Drop overlay (sibling, absolute-positioned). The outer Box sets
          position:relative so the overlay's `inset:0` works. */}
      {allowAttachments && <DropOverlay visible={isDragging} />}
    </Box>
  );
};

export default HomeChatSearchBar;
